/** @module Scan screen: session setup, then the full-screen camera with strip and shutter. */
import { Link } from "@tanstack/react-router";
import {
  CircleHelp,
  Flashlight,
  Image,
  List,
  Settings2,
  ThumbsDown,
  ThumbsUp,
  TriangleAlert,
  WifiOff,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { type FeedbackDraft, FeedbackSheet } from "@/components/feedback-sheet";
import { PageHeader } from "@/components/page-header";
import { ScanStatus, Segmented, Switch } from "@/components/ui/controls";
import { Sheet } from "@/components/ui/overlays";
import { SAME_CARD_CHANGE } from "@/features/scan/auto-capture";
import { CAMERA_WIDTH, grabFrame } from "@/features/scan/camera";
import { CorrectionSheet, entryStatus } from "@/features/scan/correction-sheet";
import { FirstRunSheet, useScanIntro } from "@/features/scan/first-run-sheet";
import { GuideFrame, moved, resized } from "@/features/scan/guide-frame-overlay";
import { cardShaped, guideRect, type Rect, toQuad } from "@/features/scan/guide-frame";
import { counts, DiagnosticsPanel, type ShotInfo } from "@/features/scan/scan-panels";
import { PHOTO_ACCEPT, splitPhotos } from "@/features/scan/photo-files";
import { type SessionChoice, SessionForm } from "@/features/scan/session-sheet";
import { FPS, type ShotReading, useAutoCapture } from "@/features/scan/use-auto-capture";
import { useCamera } from "@/features/scan/use-camera";
import { type ScanEntry, useScanSession } from "@/features/scan/use-scan-session";
import {
  insideOf,
  MAX_ZOOM,
  MIN_ZOOM,
  panned,
  toPicture,
  transformOf,
  type View,
  WHOLE,
  withMargin,
  zoomed,
} from "@/features/scan/view-box";
import { isLogging, log, setLogging, setLogScreen } from "@/lib/client-log";
import { signalResult, useFeedbackEnabled } from "@/lib/feedback";
import { useOpenReviews } from "@/lib/review-count";

const CAMERA_KEY = "poke-scan.scan.camera";
const FRAME_KEY = "poke-scan.scan.frame";
const VIEW_KEY = "poke-scan.scan.view";
const EMPTY_RECT = { x: 0.09, y: 0.09, width: 0.82, height: 0.82 };
const CROP_KEY = "poke-scan.scan.crop";
const DELAY_KEY = "poke-scan.scan.delay";
const BENCHMARK_KEY = "poke-scan.scan.benchmark";
const AUTO_KEY = "poke-scan.scan.auto";
/** Seconds the card has to lie still before the shot; long enough for the hand to leave. */
const DELAYS = [0.5, 1, 1.5, 2] as const;
const DEFAULT_DELAY = 1;
/** The strip shows the newest photos; older ones are counted above it. */
const STRIP = 12;

function rememberedDelay(): number {
  const saved = Number(remembered(DELAY_KEY));
  return DELAYS.some((delay) => delay === saved) ? saved : DEFAULT_DELAY;
}

/** Private windows refuse storage; then the setting simply does not survive a reload. */
function remembered(key: string): string {
  try {
    return localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

function remember(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Not worth an error message.
  }
}

/** One photo of the run: thumbnail with its status; a tap opens the correction. */
function StripItem({ entry, onOpen }: { entry: ScanEntry; onOpen: () => void }) {
  const { t } = useTranslation();
  const doubt = entry.repeat && !entry.booked;
  return (
    <li>
      <button
        type="button"
        className="strip-item block"
        aria-label={t(`scan.strip.${entryStatus(entry)}`, { card: entry.printId ?? "" })}
        onClick={onOpen}
      >
        <img
          src={entry.thumbnail}
          alt=""
          width={46}
          height={64}
          className={`card-shape w-full object-cover ${
            doubt ? "outline-2 outline-offset-1 outline-(--warn)" : ""
          }`}
        />
        <ScanStatus state={entryStatus(entry)} />
      </button>
    </li>
  );
}

export function ScanPage() {
  const { t, i18n } = useTranslation();
  const [stage, setStage] = useState<"setup" | "camera">("setup");
  const [choice, setChoice] = useState<SessionChoice>(() => ({
    language: i18n.resolvedLanguage ?? "de",
    setHint: null,
    setLabel: null,
    benchmark: remembered(BENCHMARK_KEY) !== "off",
    location: null,
  }));
  const [cameraOn, setCameraOn] = useState(false);
  // A holder wants the automatic, a hand often the button; the device remembers which.
  const [auto, setAutoState] = useState(() => remembered(AUTO_KEY) !== "off");
  const setAuto = (next: boolean) => {
    setAutoState(next);
    remember(AUTO_KEY, next ? "on" : "off");
  };
  const intro = useScanIntro();
  const [showIntro, setShowIntro] = useState(false);
  // After a run with photos: one quiet question, gone once answered or dismissed.
  const [askAbout, setAskAbout] = useState<{ sessionId: string } | null>(null);
  const [feedbackDraft, setFeedbackDraft] = useState<FeedbackDraft | null>(null);
  // Shows the readout and sends every frame; the photos themselves are always reported.
  const [diagnostics, setDiagnostics] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [correcting, setCorrecting] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  // Picked files that are no photo we accept; said once, not silently skipped.
  const [rejected, setRejected] = useState(0);
  const video = useRef<HTMLVideoElement | null>(null);
  // Which camera and where the frame sits are worth remembering: a holder does not move.
  const [deviceId, setDeviceId] = useState(() => remembered(CAMERA_KEY));
  const {
    error: cameraError,
    available,
    torchAvailable,
    torch,
    toggleTorch,
  } = useCamera(video, cameraOn, { deviceId: deviceId || undefined, idealWidth: CAMERA_WIDTH });
  const session = useScanSession({
    language: choice.language,
    setHint: choice.setHint,
    benchmark: choice.benchmark,
    location: choice.location,
  });
  const openReviews = useOpenReviews(true);
  const feedback = useFeedbackEnabled();
  // Results already announced; each one rings once, the moment the server has it.
  const announced = useRef(new Set<string>());
  const [busy, setBusy] = useState(false);
  // A failed capture leaves the detector disarmed; arming it again needs a new signal.
  const [attempt, setAttempt] = useState(0);
  // Non-zero means: remember the empty surface as of this moment (holder or table).
  const [rememberAt, setRememberAt] = useState(0);
  // The camera decides the shape of the picture; the box follows it so that nothing is
  // cropped away and the frame on screen is the frame the detector measures.
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const [frame, setFrame] = useState<Rect | null>(() => {
    const saved = remembered(FRAME_KEY);
    return saved ? (JSON.parse(saved) as Rect) : null;
  });
  // Photographing only the frame keeps fingers and neighbouring cards out of the picture.
  const [cropToFrame, setCropToFrame] = useState(() => remembered(CROP_KEY) !== "off");
  const [delay, setDelay] = useState(rememberedDelay);
  const [lastShot, setLastShot] = useState<ShotInfo | null>(null);
  // Tells the shutter a card was taken by hand, so it does not take it again.
  const [manualShots, setManualShots] = useState(0);
  const [view, setView] = useState<View>(() => {
    const saved = remembered(VIEW_KEY);
    return saved ? (JSON.parse(saved) as View) : WHOLE;
  });

  // The server log is how scans get debugged later: this screen always reports what it
  // photographed; every single frame only while the readout is switched on.
  useEffect(() => {
    const before = isLogging();
    setLogging(true, "info");
    setLogScreen("scan");
    return () => {
      setLogging(before);
      setLogScreen(null);
    };
  }, []);

  // Saving happens after the change, not inside it: a drag changes the value many times.
  useEffect(() => {
    if (frame) remember(FRAME_KEY, JSON.stringify(frame));
  }, [frame]);

  useEffect(() => {
    remember(VIEW_KEY, JSON.stringify(view));
  }, [view]);

  // On screen the frame sits inside the visible cut-out; the detector needs it in the
  // camera picture, so everything goes through the same conversion.
  // Dragging starts from the frame as it is on screen, not from the stored fractions.
  const shaped = (rect: Rect | null) =>
    rect && size ? cardShaped(rect, size.width, size.height) : rect;
  // A stored frame is reshaped to the current camera format, otherwise it stops being card shaped.
  const onScreen = size
    ? frame
      ? cardShaped(frame, size.width, size.height)
      : guideRect(size.width, size.height)
    : frame;
  const inPicture = onScreen ? toPicture(view, onScreen) : undefined;

  useEffect(() => {
    const element = video.current;
    if (!element || !cameraOn) return;
    const read = () => {
      if (element.videoWidth > 0) {
        setSize({ width: element.videoWidth, height: element.videoHeight });
      }
    };
    read();
    element.addEventListener("loadedmetadata", read);
    element.addEventListener("resize", read);
    return () => {
      element.removeEventListener("loadedmetadata", read);
      element.removeEventListener("resize", read);
    };
  }, [cameraOn, stage]);

  const shoot = useCallback(
    async (reading?: ShotReading) => {
      setBusy(true);
      setFailure(null);
      try {
        // Either the whole visible cut-out, or just the frame with a little room around it.
        const shown = onScreen ?? EMPTY_RECT;
        const wanted = cropToFrame ? withMargin(shown) : { x: 0, y: 0, width: 1, height: 1 };
        const still = await grabFrame(video.current, undefined, undefined, toPicture(view, wanted));
        if (!still) {
          setFailure("noFrame");
          return;
        }
        if (!reading) setManualShots((count) => count + 1);
        // Tell the server where the card should be, in the pixels of the photo it gets.
        const inPhoto = insideOf(wanted, shown);
        const hint = toQuad(inPhoto, still.width, still.height);
        const info: ShotInfo = {
          trigger: reading ? "auto" : "manual",
          photo: `${still.width}x${still.height}`,
          kilobytes: Math.round(still.photo.size / 1024),
          settleMs: reading ? reading.settle_ms : null,
          at: Date.now(),
        };
        setLastShot(info);
        // Everything needed to tell a bad shot from a bad recognition: logged, and kept
        // with the scan itself so the admin list shows it next to the photo.
        const shot = {
          trigger: info.trigger,
          photo: info.photo,
          kb: info.kilobytes,
          video: video.current ? `${video.current.videoWidth}x${video.current.videoHeight}` : null,
          delay_s: delay,
          crop: cropToFrame,
          zoom: Math.round(view.zoom * 10) / 10,
          surface: reading ? reading.novelty !== null : null,
          ...(reading ?? {}),
        };
        log("scan.shot", shot);
        const repeat = reading?.change != null && reading.change < SAME_CARD_CHANGE;
        await session.capture(still.photo, hint, repeat, shot);
      } catch (cause) {
        // A silent failure leaves a dead button and no explanation.
        const name = cause instanceof Error ? cause.name : "unknown";
        log("scan.capture_error", { name }, "error");
        setFailure(name);
        setAttempt((current) => current + 1);
      } finally {
        setBusy(false);
      }
    },
    [session, video, onScreen, view, cropToFrame, delay],
  );

  const detection = useAutoCapture(
    video,
    cameraOn && auto,
    (reading) => {
      void shoot(reading);
    },
    attempt,
    inPicture,
    rememberAt,
    Math.round(delay * FPS),
    manualShots,
  );

  useEffect(() => {
    for (const entry of session.entries) {
      if (entry.state !== "done" && entry.state !== "failed") continue;
      if (announced.current.has(entry.key)) continue;
      announced.current.add(entry.key);
      if (!feedback) continue;
      const status = entryStatus(entry);
      signalResult(status === "auto" ? "match" : status === "review" ? "review" : "none");
    }
  }, [session.entries, feedback]);

  // Space takes a photo at a desk; nobody reaches for the mouse between two cards.
  useEffect(() => {
    if (!cameraOn) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== " " || event.repeat || document.querySelector("dialog[open]")) return;
      const target = event.target as HTMLElement | null;
      if (target && ["INPUT", "SELECT", "TEXTAREA", "BUTTON"].includes(target.tagName)) return;
      event.preventDefault();
      if (!busy) void shoot();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [cameraOn, busy, shoot]);

  const upload = async (files: File[]) => {
    setBusy(true);
    try {
      const { accepted, rejected } = splitPhotos(files);
      setRejected(rejected.length);
      for (const file of accepted) {
        await session.capture(file, undefined, false, {
          trigger: "file",
          kb: Math.round(file.size / 1024),
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const endSession = () => {
    if (session.sessionId && session.entries.length > 0) {
      setAskAbout({ sessionId: session.sessionId });
    }
    setCameraOn(false);
    setStage("setup");
    session.restart();
  };
  const closeIntro = () => {
    setShowIntro(false);
    intro.markSeen();
  };

  const updateChoice = (next: SessionChoice) => {
    setChoice(next);
    remember(BENCHMARK_KEY, next.benchmark ? "on" : "off");
  };

  if (stage === "setup") {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader
          title={t("nav.scan")}
          global={false}
          actions={
            <button
              type="button"
              className="icon-btn"
              aria-label={t("scan.intro.open")}
              title={t("scan.intro.open")}
              onClick={() => {
                setShowIntro(true);
              }}
            >
              <CircleHelp />
            </button>
          }
        />
        {askAbout ? (
          <div className="panel row-between w-full max-w-xl gap-3">
            <span>{t("feedback.howWasIt")}</span>
            <span className="row gap-2">
              <button
                type="button"
                className="icon-btn"
                aria-label={t("feedback.good")}
                onClick={() => {
                  setFeedbackDraft({
                    rating: 1,
                    kind: "praise",
                    scanSessionId: askAbout.sessionId,
                  });
                  setAskAbout(null);
                }}
              >
                <ThumbsUp />
              </button>
              <button
                type="button"
                className="icon-btn"
                aria-label={t("feedback.bad")}
                onClick={() => {
                  setFeedbackDraft({ rating: -1, kind: "bug", scanSessionId: askAbout.sessionId });
                  setAskAbout(null);
                }}
              >
                <ThumbsDown />
              </button>
              <button
                type="button"
                className="icon-btn"
                aria-label={t("feedback.later")}
                onClick={() => {
                  setAskAbout(null);
                }}
              >
                <X />
              </button>
            </span>
          </div>
        ) : null}
        <FirstRunSheet open={intro.due || showIntro} onClose={closeIntro} />
        <FeedbackSheet
          open={feedbackDraft !== null}
          draft={feedbackDraft ?? {}}
          onClose={() => {
            setFeedbackDraft(null);
          }}
        />
        <div className="panel panel-lg w-full max-w-xl">
          <SessionForm
            value={choice}
            onChange={updateChoice}
            onCamera={() => {
              setStage("camera");
              setCameraOn(true);
            }}
            onUpload={(files) => {
              setStage("camera");
              void upload(files);
            }}
          />
        </div>
      </div>
    );
  }

  const count = counts(session.entries);
  const working = count.total - count.finished - count.failed;
  const correctingEntry = session.entries.find((entry) => entry.key === correcting) ?? null;
  const cameraLabel = available.find((device) => device.deviceId === deviceId)?.label;
  const ratio = size ? size.width / size.height : 3 / 4;

  return (
    <div className="cam-screen fixed inset-0 z-40 flex flex-col md:relative md:inset-auto md:z-auto md:h-[calc(100dvh-4rem)] md:overflow-hidden md:rounded-[var(--radius-lg)]">
      {/* Top: leave, what this run assumes, auto or manual, light, settings. */}
      <div className="relative z-10 flex flex-col gap-2 px-4 pt-[max(12px,env(safe-area-inset-top))] pb-2">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            className="cam-btn"
            aria-label={t("scan.endSession")}
            onClick={endSession}
          >
            <X />
          </button>
          <span className="cam-pill min-w-0">
            <span className="lang border-white/50 bg-transparent text-inherit">
              {choice.language.toUpperCase()}
            </span>
            <span className="truncate">{choice.setLabel ?? t("scan.allSets")}</span>
          </span>
          <span className="cam-seg" role="group" aria-label={t("scan.mode")}>
            <button
              type="button"
              className="cam-seg-item"
              aria-pressed={auto}
              onClick={() => {
                setAuto(true);
              }}
            >
              {t("scan.autoShort")}
            </button>
            <button
              type="button"
              className="cam-seg-item"
              aria-pressed={!auto}
              onClick={() => {
                setAuto(false);
              }}
            >
              {t("scan.manual")}
            </button>
          </span>
          <span className="flex gap-2">
            {torchAvailable ? (
              <button
                type="button"
                className="cam-btn"
                aria-label={t("scan.torch")}
                aria-pressed={torch}
                onClick={() => {
                  void toggleTorch();
                }}
              >
                <Flashlight />
              </button>
            ) : null}
            <button
              type="button"
              className="cam-btn"
              aria-label={t("scan.cameraSettings")}
              onClick={() => {
                setSettingsOpen(true);
              }}
            >
              <Settings2 />
            </button>
          </span>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          {cameraOn && auto ? (
            <button
              type="button"
              className="cam-pill"
              aria-pressed={detection.surface}
              onClick={() => {
                setRememberAt(detection.surface ? 0 : Date.now());
              }}
            >
              {detection.surface ? t("scan.forgetSurface") : t("scan.rememberSurface")}
            </button>
          ) : null}
          {!session.online ? (
            <span className="cam-pill">
              <WifiOff />
              {t("scan.offline")}
            </span>
          ) : null}
          {failure ? (
            <span role="alert" className="cam-pill">
              <TriangleAlert />
              {t("scan.captureFailed", { reason: failure })}
            </span>
          ) : null}
          {rejected > 0 ? (
            <span role="alert" className="cam-pill">
              <TriangleAlert />
              {t("scan.filesRejected", { count: rejected })}
            </span>
          ) : null}
          {cameraError ? (
            <span role="alert" className="cam-pill">
              <TriangleAlert />
              {t("scan.cameraError")}
            </span>
          ) : null}
        </div>
        {diagnostics && cameraOn ? (
          <div className="absolute inset-x-4 top-full z-20 max-h-[40dvh] overflow-y-auto rounded-[var(--radius-md)] bg-black/70 text-white">
            <DiagnosticsPanel
              detection={detection}
              lastShot={lastShot}
              settings={{
                delay,
                crop: cropToFrame,
                zoom: view.zoom,
                // An unnamed camera reports an empty label, which deserves the default name too.
                camera: cameraLabel?.length ? cameraLabel : t("scan.cameraDefault"),
              }}
            />
          </div>
        ) : null}
      </div>

      {/* The picture, as large as the screen allows, always in the camera's own shape. */}
      <div className="cam-stage relative flex min-h-0 grow items-center justify-center py-2">
        <div className="cam-box" style={{ ["--cam-ratio" as string]: String(ratio) }}>
          <div className="absolute inset-0 overflow-hidden">
            <video
              ref={video}
              playsInline
              muted
              className="h-full w-full object-contain"
              style={{ transform: transformOf(view), transformOrigin: "center" }}
            />
          </div>
          {cameraOn ? (
            <GuideFrame
              readiness={auto ? detection.readiness : "off"}
              progress={detection.progress}
              rect={onScreen}
              // Every step is applied to the current value, not to the one this render
              // captured: otherwise a long drag keeps only its last few pixels.
              onMove={(dx, dy) => {
                setFrame((current) => moved(shaped(current) ?? onScreen ?? EMPTY_RECT, dx, dy));
              }}
              onResize={(dx, ratioNow) => {
                setFrame((current) =>
                  resized(shaped(current) ?? onScreen ?? EMPTY_RECT, dx, ratioNow),
                );
              }}
              onPan={(dx, dy) => {
                setView((current) => panned(current, dx, dy));
              }}
            />
          ) : null}
        </div>
        {!cameraOn ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
            <p className="t-sm max-w-xs opacity-80">{t("scan.cameraHint")}</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setCameraOn(true);
              }}
            >
              {t("scan.startCamera")}
            </button>
          </div>
        ) : null}
      </div>

      {/* Bottom: what the run produced so far, the strip, and the shutter. */}
      <div className="flex flex-col gap-[14px] px-4 pt-3 pb-[max(24px,env(safe-area-inset-bottom))]">
        <div className="row-between t-sm" aria-live="polite">
          <span>
            {count.total === 0 ? (
              t("scan.none")
            ) : (
              <>
                <b className="num">{count.total}</b> {t("scan.captured")}
                {working > 0 ? ` · ${t("scan.working", { count: working })}` : ""}
              </>
            )}
          </span>
          <span className="row gap-[10px]">
            {(
              [
                ["auto", count.booked],
                ["review", count.review],
                ["none", count.noMatch + count.failed],
              ] as const
            ).map(([state, value]) => (
              <span key={state} className="row gap-1" title={t(`scan.counter.${state}`)}>
                <ScanStatus state={state} className="size-[18px] text-[10px]" />
                <span className="num">{value}</span>
                <span className="sr-only">{t(`scan.counter.${state}`)}</span>
              </span>
            ))}
          </span>
        </div>
        {session.entries.length > 0 ? (
          <ul
            className="flex gap-2 overflow-x-auto pt-1 pr-2 pb-2"
            aria-label={t("scan.strip.label")}
          >
            {session.entries.slice(0, STRIP).map((entry) => (
              <StripItem
                key={entry.key}
                entry={entry}
                onOpen={() => {
                  setCorrecting(entry.key);
                }}
              />
            ))}
          </ul>
        ) : null}
        <div className="flex items-center justify-between">
          <label className="cam-btn cursor-pointer" aria-label={t("scan.upload")}>
            <Image />
            <input
              type="file"
              accept={PHOTO_ACCEPT}
              multiple
              className="sr-only"
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                // Free the input right away so the same files can be picked again.
                event.target.value = "";
                void upload(files);
              }}
            />
          </label>
          <button
            type="button"
            className="shutter"
            aria-label={t("scan.capture")}
            disabled={!cameraOn || busy}
            onClick={() => {
              void shoot();
            }}
          />
          <Link to="/review" className="cam-btn relative" aria-label={t("scan.toReview")}>
            <List />
            {openReviews > 0 ? (
              <span className="badge badge-count absolute -top-1 -right-1.5">{openReviews}</span>
            ) : null}
          </Link>
        </div>
      </div>

      <CorrectionSheet
        entry={correctingEntry}
        onClose={() => {
          setCorrecting(null);
        }}
        onDismiss={(entry) => {
          void session.remove(entry.key);
        }}
        onBooked={(entry) => {
          session.settle(entry.key, { booked: true });
        }}
        onReopened={(entry) => {
          // Open again: the sheet stays and shows the suggestions to choose from.
          session.settle(entry.key, { booked: false, decision: "review" });
        }}
      />

      <Sheet
        open={settingsOpen}
        onClose={() => {
          setSettingsOpen(false);
        }}
        label={t("scan.cameraSettings")}
      >
        <div className="flex flex-col gap-5">
          <h2 className="t-h3">{t("scan.cameraSettings")}</h2>
          <div className="flex flex-col gap-2">
            <span className="field-label">{t("scan.delay")}</span>
            <Segmented
              label={t("scan.delay")}
              value={String(delay)}
              onChange={(next) => {
                setDelay(Number(next));
                remember(DELAY_KEY, next);
              }}
              options={DELAYS.map((option) => ({
                value: String(option),
                label: t("scan.delaySeconds", {
                  seconds: option.toLocaleString(i18n.resolvedLanguage),
                }),
              }))}
            />
          </div>
          {cameraOn ? (
            <label className="field">
              <span className="field-label">
                {t("scan.zoom")} · <span className="num">{view.zoom.toFixed(1)}×</span>
              </span>
              <input
                type="range"
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={0.1}
                value={view.zoom}
                onChange={(event) => {
                  const wanted = Number(event.target.value);
                  setView((current) => zoomed(current, wanted));
                }}
              />
            </label>
          ) : null}
          {available.length > 1 ? (
            <label className="field">
              <span className="field-label">{t("scan.camera")}</span>
              <select
                className="input"
                value={deviceId}
                onChange={(event) => {
                  setDeviceId(event.target.value);
                  remember(CAMERA_KEY, event.target.value);
                }}
              >
                <option value="">{t("scan.cameraDefault")}</option>
                {available.map((device, index) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || t("scan.cameraNumber", { number: index + 1 })}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <div className="row-between min-h-11">
            <span>{t("scan.cropToFrame")}</span>
            <Switch
              checked={cropToFrame}
              label={t("scan.cropToFrame")}
              onChange={(next) => {
                setCropToFrame(next);
                remember(CROP_KEY, next ? "on" : "off");
              }}
            />
          </div>
          <div className="row-between min-h-11">
            <span className="flex flex-col">
              <span>{t("scan.diagnostics")}</span>
              <span className="t-xs t-muted">{t("scan.diagnosticsHint")}</span>
            </span>
            <Switch
              checked={diagnostics}
              label={t("scan.diagnostics")}
              onChange={(next) => {
                setDiagnostics(next);
                setLogging(true, next ? "debug" : "info");
              }}
            />
          </div>
          <p className="t-sm t-muted">{t("scan.frameHint")}</p>
          <button
            type="button"
            className="btn btn-ghost btn-sm self-start"
            onClick={() => {
              setSettingsOpen(false);
              setShowIntro(true);
            }}
          >
            <CircleHelp />
            {t("scan.intro.open")}
          </button>
          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={() => {
              setSettingsOpen(false);
            }}
          >
            {t("scan.correct.done")}
          </button>
        </div>
      </Sheet>
      <FirstRunSheet open={showIntro} onClose={closeIntro} />
    </div>
  );
}
